import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { slaApi } from '../../api/sla';
import toast from 'react-hot-toast';
import { Plus, Pencil, Trash2, X, Shield } from 'lucide-react';
import type { SLAPolicy, SLAPriorityConfig } from '../../types';
import EmptyState from '../EmptyState';
import { useClassifications } from '../../hooks/useClassifications';

const DEFAULT_PRIORITIES: SLAPolicy['priorities'] = {
  critical: { responseMinutes: 15, resolutionMinutes: 120 },
  high: { responseMinutes: 60, resolutionMinutes: 480 },
  normal: { responseMinutes: 240, resolutionMinutes: 1440 },
  low: { responseMinutes: 480, resolutionMinutes: 2880 },
};

interface FormData {
  name: string;
  description: string;
  isDefault: boolean;
  businessHoursOnly: boolean;
  priorities: SLAPolicy['priorities'];
}

const emptyForm: FormData = {
  name: '',
  description: '',
  isDefault: false,
  businessHoursOnly: true,
  priorities: DEFAULT_PRIORITIES,
};

function formatMinutes(m: number): string {
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  const rem = m % 60;
  if (h < 24) return rem ? `${h}h ${rem}m` : `${h}h`;
  const d = Math.floor(h / 24);
  const remH = h % 24;
  return remH ? `${d}d ${remH}h` : `${d}d`;
}

export default function SLAPoliciesTab() {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<string | null>(null); // 'new' or policy id
  const [form, setForm] = useState<FormData>(emptyForm);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);

  const { byKind: classificationsOf } = useClassifications();
  const priorityDefs = classificationsOf('priority');

  const { data: policies = [] } = useQuery({
    queryKey: ['sla'],
    queryFn: () => slaApi.list(),
  });

  const createMutation = useMutation({
    mutationFn: (data: FormData) => slaApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sla'] });
      toast.success('SLA policy created');
      closeForm();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: FormData }) => slaApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sla'] });
      toast.success('SLA policy updated');
      closeForm();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => slaApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sla'] });
      toast.success('SLA policy deleted');
      setDeleteConfirm(null);
    },
    onError: (e: Error) => toast.error(e.message),
  });

  function closeForm() {
    setEditing(null);
    setForm(emptyForm);
  }

  function startEdit(policy: any) {
    setEditing(policy.id ?? policy._id);
    setForm({
      name: policy.name,
      description: policy.description || '',
      isDefault: policy.isDefault,
      businessHoursOnly: policy.businessHoursOnly,
      priorities: policy.priorities || DEFAULT_PRIORITIES,
    });
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!form.name.trim()) return toast.error('Name is required');
    if (editing === 'new') {
      createMutation.mutate(form);
    } else {
      updateMutation.mutate({ id: editing!, data: form });
    }
  }

  function updatePriority(level: keyof SLAPolicy['priorities'], field: keyof SLAPriorityConfig, value: number) {
    setForm(prev => ({
      ...prev,
      priorities: {
        ...prev.priorities,
        [level]: { ...prev.priorities[level], [field]: value },
      },
    }));
  }

  const isSaving = createMutation.isPending || updateMutation.isPending;
  // A policy sets targets per priority, and priorities are per-company now.
  // Every def is listed (not just active ones) so a policy that already has
  // targets for a retired priority still shows and can be edited.
  const priorityLevels = priorityDefs.map(d => d.key) as (keyof SLAPolicy['priorities'])[];
  const priorityLabels: Record<string, string> = Object.fromEntries(priorityDefs.map(d => [d.key, d.label]));
  const priorityColors: Record<string, string> = Object.fromEntries(
    priorityDefs.map(d => [
      d.key,
      d.color === 'error' ? 'text-err'
        : d.color === 'warning' ? 'text-warn'
        : d.color === 'primary' ? 'text-primary'
        : d.color === 'ok' ? 'text-ok'
        : 'text-muted',
    ]),
  );

  return (
    <div className="card p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="section-title">SLA Policies</h2>
        {!editing && (
          <button
            className="btn btn-primary inline-flex items-center gap-2"
            onClick={() => { setEditing('new'); setForm(emptyForm); }}
          >
            <Plus className="w-4 h-4" />
            Add Policy
          </button>
        )}
      </div>

      {editing && (
        <form onSubmit={handleSubmit} className="mb-6 p-4 border border-line rounded-lg space-y-4 bg-row-odd">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold text-fg">
              {editing === 'new' ? 'New SLA Policy' : 'Edit SLA Policy'}
            </h3>
            <button type="button" onClick={closeForm} className="text-muted hover:text-fg">
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="label">Name</label>
              <input
                className="input"
                value={form.name}
                onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                placeholder="e.g. Standard, Premium, Enterprise"
              />
            </div>
            <div>
              <label className="label">Description</label>
              <input
                className="input"
                value={form.description}
                onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                placeholder="Optional description"
              />
            </div>
          </div>

          <div className="flex gap-6">
            <label className="flex items-center gap-2 text-sm text-fg">
              <input
                type="checkbox"
                checked={form.isDefault}
                onChange={e => setForm(f => ({ ...f, isDefault: e.target.checked }))}
                className="rounded accent-primary"
              />
              Default policy
            </label>
            <label className="flex items-center gap-2 text-sm text-fg">
              <input
                type="checkbox"
                checked={form.businessHoursOnly}
                onChange={e => setForm(f => ({ ...f, businessHoursOnly: e.target.checked }))}
                className="rounded accent-primary"
              />
              Business hours only
            </label>
          </div>

          <div>
            <label className="label mb-2">Response &amp; Resolution Times (minutes)</label>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
              {priorityLevels.map(level => (
                <div key={level} className="p-3 border border-line rounded-lg">
                  <div className={`text-sm font-semibold mb-2 ${priorityColors[level]}`}>{priorityLabels[level] ?? level}</div>
                  <div className="space-y-2">
                    <div>
                      <label className="text-xs text-muted">Response (min)</label>
                      <input
                        type="number"
                        min={1}
                        className="input text-sm"
                        value={form.priorities[level].responseMinutes}
                        onChange={e => updatePriority(level, 'responseMinutes', parseInt(e.target.value) || 0)}
                      />
                    </div>
                    <div>
                      <label className="text-xs text-muted">Resolution (min)</label>
                      <input
                        type="number"
                        min={1}
                        className="input text-sm"
                        value={form.priorities[level].resolutionMinutes}
                        onChange={e => updatePriority(level, 'resolutionMinutes', parseInt(e.target.value) || 0)}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="flex gap-2 pt-2">
            <button type="submit" className="btn btn-primary" disabled={isSaving}>
              {isSaving ? 'Saving...' : editing === 'new' ? 'Create Policy' : 'Update Policy'}
            </button>
            <button type="button" className="btn btn-ghost" onClick={closeForm}>Cancel</button>
          </div>
        </form>
      )}

      {(policies as any[]).length === 0 && !editing ? (
        <EmptyState icon={<Shield className="w-6 h-6" />}>
          No SLA policies yet. Click <strong>Add Policy</strong> to define response and resolution time targets.
        </EmptyState>
      ) : (
        <div className="space-y-2">
          {(policies as any[]).map(p => (
            <div key={p.id ?? p._id} className="flex items-start justify-between py-3 border-b border-line-light last:border-0">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-medium text-fg">{p.name}</span>
                  {p.isDefault && <span className="badge text-primary">Default</span>}
                  {p.businessHoursOnly && <span className="badge text-muted">Business hrs</span>}
                </div>
                {p.description && <p className="text-sm text-muted mt-0.5">{p.description}</p>}
                {p.priorities && (
                  <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1.5 text-xs text-muted">
                    {priorityLevels.map(level => p.priorities[level] && (
                      <span key={level}>
                        <span className={`font-medium ${priorityColors[level]}`}>{priorityLabels[level] ?? level}</span>
                        {' '}R: {formatMinutes(p.priorities[level].responseMinutes)} / S: {formatMinutes(p.priorities[level].resolutionMinutes)}
                      </span>
                    ))}
                  </div>
                )}
              </div>
              <div className="flex items-center gap-1 ml-4 shrink-0">
                <button
                  className="p-1.5 text-muted hover:text-primary rounded transition-colors"
                  onClick={() => startEdit(p)}
                  title="Edit"
                >
                  <Pencil className="w-4 h-4" />
                </button>
                {deleteConfirm === (p.id ?? p._id) ? (
                  <div className="flex items-center gap-1">
                    <button
                      className="text-xs btn btn-danger py-1 px-2"
                      onClick={() => deleteMutation.mutate(p.id ?? p._id)}
                      disabled={deleteMutation.isPending}
                    >
                      Confirm
                    </button>
                    <button
                      className="text-xs btn btn-ghost py-1 px-2"
                      onClick={() => setDeleteConfirm(null)}
                    >
                      Cancel
                    </button>
                  </div>
                ) : (
                  <button
                    className="p-1.5 text-muted hover:text-err rounded transition-colors"
                    onClick={() => setDeleteConfirm(p.id ?? p._id)}
                    title="Delete"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
